import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as associateService from '../../services/associate-service';
import type { MentorDetail } from '../../services/associate-service';

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const statusBadge = (status: string): string => {
  if (status === 'approved') return 'bg-success/15 text-success border-success/30';
  if (status === 'pending') return 'bg-warning/15 text-warning border-warning/30';
  if (status === 'rejected') return 'bg-danger/15 text-danger border-danger/30';
  return 'bg-surface text-muted border-line';
};

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const AssociateMentorProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<MentorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    associateService
      .getMentorDetail(id)
      .then((d) => {
        if (!d) setNotFound(true);
        else setDetail(d);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <p className="font-serif-display italic text-muted py-12">Loading mentor profile…</p>
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Link
          to="/app/dashboard/associate?tab=mentors"
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
        >
          <BackArrow />
          Back to mentors
        </Link>
        <div className="bg-shell border border-line rounded-card p-12 text-center">
          <p className="font-display text-2xl tracking-wide text-text uppercase mb-2">Mentor not found</p>
          <p className="font-serif-display italic text-muted">
            We couldn't find a mentor with that ID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      {/* ─── Back ─── */}
      <Link
        to="/app/dashboard/associate?tab=mentors"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
      >
        <BackArrow />
        Back to mentors
      </Link>

      {/* ─── Hero ─── */}
      <div className="bg-shell border border-line rounded-card p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div className="w-20 h-20 rounded-2xl bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-2xl flex-shrink-0">
            {initialsOf(detail.name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Mentor Profile</p>
            <h1 className="font-display text-3xl md:text-5xl tracking-wide text-text leading-tight">{detail.name}</h1>
            <p className="font-serif-display italic text-muted text-lg md:text-xl mt-2">
              {detail.title}
              {detail.company && <> · {detail.company}</>}
            </p>
            <div className="flex items-center gap-2 flex-wrap mt-4">
              {detail.region && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface border border-line text-xs text-muted">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  {detail.region}
                </span>
              )}
              {detail.experience && (
                <span className="px-3 py-1 rounded-full bg-chart/10 text-chart border border-chart/20 text-xs font-medium">
                  {detail.experience} of experience
                </span>
              )}
              {detail.status === 'approved' && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusBadge(detail.status)}`}>
                  Verified Mentor
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Grid: main content / sidebar ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main column */}
        <div className="space-y-6">
          {/* Bio */}
          {detail.bio && (
            <section className="bg-shell border border-line rounded-card p-6 md:p-7">
              <div className="text-[10px] uppercase tracking-wider text-muted mb-3">About</div>
              <p className="font-serif-display italic text-text text-lg leading-relaxed">{detail.bio}</p>
            </section>
          )}

          {/* Expertise / Specialties / Industries */}
          <section className="bg-shell border border-line rounded-card p-6 md:p-7 space-y-5">
            <PillGroup label="Expertise" items={detail.expertise} accent="chart" />
            <PillGroup label="Specialties" items={detail.specialties} accent="success" />
            <PillGroup label="Industries" items={detail.industry} accent="warning" />
          </section>

          {/* Stats row */}
          <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatTile label="Mentees" value={detail.menteesCount} sub="founders mentored" />
            <StatTile label="Sessions Led" value={detail.sessionsLed} sub="group sessions" />
            <StatTile label="Experience" value={detail.experienceYears || '—'} sub="years in the field" />
          </section>

          {/* Mentees */}
          {detail.mentees.length > 0 && (
            <section>
              <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-4">Mentees</h2>
              <div className="bg-shell border border-line rounded-card divide-y divide-line">
                {detail.mentees.map((m) => (
                  <div key={m.founderId} className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                        {initialsOf(m.founderName)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-text text-sm truncate">{m.founderName}</div>
                        {m.startupName && <div className="text-xs text-muted truncate">{m.startupName}</div>}
                      </div>
                    </div>
                    {m.startupId && (
                      <Link
                        to={`/app/associate/startup/${m.startupId}`}
                        className="text-xs text-chart hover:text-chart/80 font-medium transition-colors whitespace-nowrap"
                      >
                        View startup →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recent sessions */}
          {detail.recentSessions.length > 0 && (
            <section>
              <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-4">Recent Sessions Led</h2>
              <ol className="relative border-l border-line pl-6 space-y-4">
                {detail.recentSessions.map((s) => (
                  <li key={s.id} className="relative">
                    <span
                      className={`absolute -left-[31px] top-1 w-3 h-3 rounded-full border-2 border-bg ${
                        s.status === 'completed' ? 'bg-success' : s.status === 'scheduled' ? 'bg-chart' : 'bg-warning'
                      }`}
                    />
                    <div className="text-sm text-text font-medium">{s.title}</div>
                    <div className="text-xs text-muted italic mt-0.5">
                      {s.attendeeCount} attendee{s.attendeeCount === 1 ? '' : 's'} · {s.status}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{formatDate(s.scheduledAt)}</div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-surface rounded-card p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-3">Contact</div>
            <dl className="space-y-3 text-sm">
              {detail.phone && <InfoRow label="Phone" value={detail.phone} />}
              {detail.linkedIn && (
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted">LinkedIn</dt>
                  <dd className="min-w-0">
                    <a
                      href={detail.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-chart hover:text-chart/80 transition-colors break-all text-right block"
                    >
                      Profile ↗
                    </a>
                  </dd>
                </div>
              )}
              {detail.twitter && (
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted">Twitter / X</dt>
                  <dd>
                    <a
                      href={detail.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-chart hover:text-chart/80 transition-colors"
                    >
                      Profile ↗
                    </a>
                  </dd>
                </div>
              )}
              {!detail.phone && !detail.linkedIn && !detail.twitter && (
                <p className="text-xs italic text-muted">Contact details unavailable.</p>
              )}
            </dl>
          </div>

          {detail.status === 'approved' && (
            <div className="bg-success/10 border border-success/20 rounded-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-success" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs font-semibold text-success uppercase tracking-wide">Verified</span>
              </div>
              <p className="text-xs text-muted">
                This mentor has been reviewed and approved by the BunyadKar admissions team.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AssociateMentorProfile;

// ── Sub-components ─────────────────────────────────────────────────────

const BackArrow: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const PillGroup: React.FC<{ label: string; items: string[]; accent: 'chart' | 'success' | 'warning' }> = ({
  label,
  items,
  accent,
}) => {
  if (items.length === 0) return null;
  const accentClass =
    accent === 'chart'
      ? 'bg-chart/10 text-chart border-chart/20'
      : accent === 'success'
      ? 'bg-success/10 text-success border-success/20'
      : 'bg-warning/10 text-warning border-warning/20';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${accentClass}`}>
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

const StatTile: React.FC<{ label: string; value: number | string; sub: string }> = ({ label, value, sub }) => (
  <div className="bg-shell border border-line rounded-card p-5">
    <div className="text-[10px] uppercase tracking-wider text-muted mb-2">{label}</div>
    <div className="text-3xl font-display text-text tabular-nums leading-none">{value}</div>
    <div className="text-xs text-muted mt-2">{sub}</div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-muted">{label}</dt>
    <dd className="text-text font-medium">{value}</dd>
  </div>
);
